import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Modal, Popover, Progress, Tag, Tooltip, message, notification } from 'antd';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import { ScaleLoader } from 'react-spinners';
import { RootState } from '../../../logic/store/Store';
import { CoveringLoader } from '../../../components/LoadingSpinner/CoveringLoader';
import { Button } from '../../../components/Button/Button';
import { Api } from '../../../logic/Api/Api';
import { NetError } from '../../../logic/Api/NetError';
import { IAccountMigrationStatus, IMigratableAccount, IStartAccountDataMigrationResult } from './models';
import { filterAccounts } from './filterAccounts';
import { useAccountMigrationEvents } from './useAccountMigrationEvents';
import { progressPercent, progressText } from './progress';

const LIST_ENDPOINT = 'supporttools/account-migrations';
const START_ENDPOINT = 'supporttools/migrate-account-data';

const errorText = (e: unknown, fallback: string): string =>
	e instanceof NetError ? e.message : (e instanceof Error ? e.message : fallback);

const formatTime = (iso: string): string =>
{
	const d = new Date(iso);
	return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const StatusTag = ({ status }: { status: IAccountMigrationStatus | null | undefined }) =>
{
	if (!status)
	{
		return <Tag>Not started</Tag>;
	}
	switch (status.Status)
	{
		case 'Running':
		{
			const p = status.Progress;
			return <Tooltip title={p
				? `Batches ${p.CompletedBatches}/${p.TotalBatches} · started ${formatTime(status.StartTime)}`
				: 'Progress not available yet'}>
				<MigrationProgress>
					<Progress
						percent={progressPercent(p)}
						size='small'
						status={p && p.FailedCopies > 0 ? 'exception' : 'active'}
						showInfo={false}
					/>
					<span className='migration-progress-text'>{progressText(p)}</span>
				</MigrationProgress>
			</Tooltip>;
		}
		case 'Completed':
			if (status.Completed === true)
			{
				return <Tag color='success'>Migrated</Tag>;
			}
			if (status.Completed === false)
			{
				return <Tag color='error'>Finished with {status.FailureCount ?? 0} failures (not completed)</Tag>;
			}
			return <Tag>Completed</Tag>;
		case 'Failed':
		case 'Terminated':
		case 'Canceled':
		case 'TimedOut':
			return <Tag color='error'>{status.Status}</Tag>;
		default:
			return <Tag>{status.Status}</Tag>;
	}
};

export const AccountMigrationTool = () =>
{
	const userAuthToken = useSelector((state: RootState) => state.auth.userAuthToken);
	const api = useMemo(() => new Api('/api/4', userAuthToken, undefined), [userAuthToken]);
	const theme = useTheme();

	const [accounts, setAccounts] = useState<IMigratableAccount[]>();
	const [loadError, setLoadError] = useState<string>();
	const [filterText, setFilterText] = useState('');
	// Rows we have just started, shown as Running until the stream reports them
	const [optimistic, setOptimistic] = useState<Record<string, IAccountMigrationStatus>>({});
	// FoundationId currently being posted
	const [starting, setStarting] = useState<string>();

	const { statuses, connected, error: streamError } = useAccountMigrationEvents(api);

	// Load the list once
	useEffect(() =>
	{
		let cancelled = false;
		async function exec()
		{
			setAccounts(undefined);
			setLoadError(undefined);
			try
			{
				const response = await api.getAsync<IMigratableAccount[]>(LIST_ENDPOINT);
				if (cancelled)
				{
					return;
				}
				if (response?.Success)
				{
					setAccounts(response.Data ?? []);
				}
				else
				{
					setLoadError(response?.ErrorMessage ?? 'Failed to load accounts');
				}
			}
			catch (e)
			{
				if (!cancelled)
				{
					setLoadError(errorText(e, 'Failed to load accounts'));
				}
			}
		}
		exec();
		return () =>
		{
			cancelled = true;
		};
	}, [api]);

	// Once the stream reports an account, its optimistic entry is redundant
	useEffect(() =>
	{
		const confirmed = Object.keys(optimistic).filter(id => statuses[id]);
		if (confirmed.length > 0)
		{
			setOptimistic(prev =>
			{
				const next = { ...prev };
				confirmed.forEach(id => delete next[id]);
				return next;
			});
		}
	}, [statuses]);

	const filtered = useMemo(() => filterAccounts(accounts ?? [], filterText), [accounts, filterText]);

	const statusFor = useCallback((a: IMigratableAccount): IAccountMigrationStatus | null =>
		statuses[a.FoundationId] ?? optimistic[a.FoundationId] ?? a.Migration, [statuses, optimistic]);

	const startMigration = useCallback(async (a: IMigratableAccount) =>
	{
		setStarting(a.FoundationId);
		try
		{
			const response = await api.postAsync<IStartAccountDataMigrationResult>(START_ENDPOINT, { FoundationId: a.FoundationId });
			if (!response?.Success)
			{
				notification.error({ message: 'Migration failed to start', description: response?.ErrorMessage ?? 'Unknown error' });
				return;
			}

			const result = response.Data;
			switch (result.Outcome)
			{
				case 'Started':
					message.success(result.Message);
					setOptimistic(prev => ({
						...prev,
						[a.FoundationId]: {
							FoundationId: a.FoundationId,
							WorkflowId: result.WorkflowId ?? '',
							RunId: result.RunId ?? '',
							Status: 'Running',
							StartTime: new Date().toISOString(),
							CloseTime: null,
							Completed: null,
							FailureCount: null,
							Progress: null,
						}
					}));
					break;
				case 'AlreadyRunning':
					message.warning(result.Message);
					break;
				case 'NoDestinationAccount':
				case 'NoSourceUsers':
					message.info(result.Message);
					setAccounts(prev => (prev ?? []).filter(x => x.FoundationId !== a.FoundationId));
					break;
				default:
					message.info(result.Message);
			}
		}
		catch (e)
		{
			notification.error({ message: 'Migration failed to start', description: errorText(e, String(e)) });
		}
		finally
		{
			setStarting(undefined);
		}
	}, [api]);

	const confirmMigration = useCallback((a: IMigratableAccount) =>
	{
		Modal.confirm({
			title: `Migrate ${a.SourceUsers.length} user(s) into ${a.AccountName}?`,
			content: <div style={{ marginLeft: -38 }}>
				<div>Data from the following users will be copied to the account user:</div>
				<ul style={{ marginTop: 8, paddingLeft: 0, listStylePosition: 'inside' }}>
					{a.SourceUsers.map(u => <li key={u.Id}>{u.Email}{u.Name ? ` (${u.Name})` : ''}</li>)}
				</ul>
				{a.HasPreviousMigration && <div>This account has been migrated before, so only new data will be copied.</div>}
			</div>,
			okText: 'Migrate',
			onOk: () => startMigration(a),
		});
	}, [startMigration]);

	const isLoading = (accounts === undefined && !loadError) || !!starting;

	return <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'max-content 1fr max-content max-content minmax(260px, max-content) max-content', gridColumnGap: 12 }}>
		<Description>
			Migrates the data of a seller&apos;s individual users into that same seller&apos;s Salesforce account user.
			It does not copy data between different sellers.<br/>
			To move growers fields from one seller to another, use the <strong>Data Transfer</strong> tool instead.
		</Description>
		<h2>
			Filter:
		</h2>
		<div style={{ gridColumnStart: 2, gridColumnEnd: 6 }}>
			<Input
				allowClear
				onChange={(e) => setFilterText(e.currentTarget.value)}
				placeholder='Search Foundation ID, account name or user email'
			/>
		</div>
		<LiveIndicator title={streamError ?? (connected ? 'Receiving live migration status' : 'Reconnecting to the migration status stream')}>
			<Dot style={{ backgroundColor: streamError ? '#473d3e' : connected ? '#52c41a' : '#bfbfbf' }} />
			<span>{streamError ? 'offline' : connected ? 'live' : 'reconnecting…'}</span>
		</LiveIndicator>
		{loadError && <ErrorBanner>{loadError}</ErrorBanner>}
		{streamError && <ErrorBanner>{streamError}</ErrorBanner>}
		<HeaderRow>
			<div>Foundation ID</div><div>Account</div><div>Users</div><div /><div>Status</div><div>Migrate</div>
		</HeaderRow>
		{
			accounts !== undefined && !loadError && filtered.length === 0 &&
			<div style={{ gridColumn: '1 / -1', padding: 8, color: '#8c8c8c' }}>
				{accounts.length === 0 ? 'No accounts are waiting for migration.' : 'No accounts match the filter.'}
			</div>
		}
		<RowGroup>
			{filtered.map(a =>
			{
				const status = statusFor(a);
				const running = status?.Status === 'Running';
				return <Row key={a.FoundationId}>
					<div>{a.FoundationId}</div>
					<div>{a.AccountName}</div>
					<div>
						<Popover
							title='Source users (most recently active first)'
							content={<ul style={{ margin: 0, paddingLeft: 16 }}>
								{a.SourceUsers.map(u => <li key={u.Id}>{u.Name ? `${u.Name} <${u.Email}>` : u.Email}</li>)}
							</ul>}
						>
							<UserCount>{a.SourceUsers.length} user{a.SourceUsers.length === 1 ? '' : 's'}</UserCount>
						</Popover>
					</div>
					<div>{a.HasPreviousMigration && <Tag>previous migration</Tag>}</div>
					<div><StatusTag status={status} /></div>
					<div>
						<Button variant='dark' disabled={running || starting === a.FoundationId} onClick={() => confirmMigration(a)}>Migrate</Button>
					</div>
				</Row>;
			})}
		</RowGroup>
		<CoveringLoader duration={100} className={isLoading ? 'loading' : ''}>
			<ScaleLoader color={theme.colors.primary} loading={true} />
		</CoveringLoader>
	</div>;
};

// Every row spans all columns and inherits the parent's tracks, so cells stay aligned across rows
// while the row itself owns a full-width background.
const SubgridRow = styled.div`
	grid-column: 1 / -1;
	display: grid;
	grid-template-columns: subgrid;
	align-items: center;

	> div {
		padding: 6px 0;
	}

	> div:first-child {
		padding-left: 12px;
	}

	> div:last-child {
		padding-right: 12px;
	}
`;

const HeaderRow = styled(SubgridRow)`
	font-weight: 600;
	border-bottom: 1px solid #d9d9d9;
`;

const RowGroup = styled.div`
	grid-column: 1 / -1;
	display: grid;
	grid-template-columns: subgrid;
`;

const Row = styled(SubgridRow)`
	&:nth-child(even) {
		background-color: #fafafa;
	}

	&:hover {
		background-color: #e8e8e8;
	}
`;

const Description = styled.p`
	grid-column: 1 / -1;
	margin: 0 0 12px;
	padding: 8px 12px;
	color: #434343;
	background-color: #f0f5ff;
	border-left: 3px solid #2f54eb;
	border-radius: 2px;
`;

const MigrationProgress = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	width: 260px;

	.ant-progress {
		margin: 0;
	}

	.migration-progress-text {
		font-size: 12px;
	}
`;

const LiveIndicator = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	white-space: nowrap;
	font-size: 12px;
	color: #595959;
	margin-bottom: .5em;
`;

const Dot = styled.span`
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
`;

const ErrorBanner = styled.div`
	grid-column: 1 / -1;
	padding: 8px;
	color: #a8071a;
	background-color: #fff1f0;
	border: 1px solid #ffa39e;
	border-radius: 4px;
	margin-bottom: 8px;
`;

const UserCount = styled.span`
	cursor: help;
	text-decoration: underline dotted;
`;
